import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import api from '../../services/api';
import LocationSelector from '../common/LocationSelector';

const ProfileEdit = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [lookups, setLookups] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch profile data
        const profileResponse = await api.get('/profile');
        setProfileData(profileResponse.data.data);

        // Fetch lookups for dropdown options
        const lookupsResponse = await api.get('/lookups?tag=profile');
        const lookupData = {};

        if (lookupsResponse.data && lookupsResponse.data.data) {
          lookupsResponse.data.data.forEach(lookup => {
            lookupData[lookup.name] = lookup.choices;
          });
        }

        setLookups(lookupData);
      } catch (error) {
        console.error('Error fetching profile data:', error);
        setError('Failed to load profile data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Validation schema
  const validationSchema = Yup.object({
    orientation: Yup.number().required('Orientation is required'),
    eyes: Yup.number().nullable(),
    body_type: Yup.number().nullable(),
    height: Yup.number().nullable(),
    ethnicity: Yup.number().nullable(),
    income: Yup.number().nullable(),
    net_worth: Yup.number().nullable(),
    children: Yup.number().nullable(),
    relationship_status: Yup.number().nullable(),
    smoker: Yup.number().nullable(),
    drinker: Yup.number().nullable(),
    pot_friendly: Yup.number().nullable(),
    party_drugs: Yup.number().nullable(),
    city: Yup.string().required('City is required'),
    region: Yup.string().required('Region/State is required'),
    country: Yup.string().required('Country is required'),
    latitude: Yup.string(),
    longitude: Yup.string(),
    about: Yup.string().max(1000, 'About me must be less than 1000 characters'),
    looking_for: Yup.string().max(1000, 'Looking for must be less than 1000 characters')
  });

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    setSaving(true);
    setSaveSuccess(false);
    setError(null);

    try {
      // Log location data being sent to the backend
      console.log('Submitting profile update with location data:', {
        city: values.city,
        region: values.region,
        country: values.country,
        latitude: values.latitude,
        longitude: values.longitude
      });

      const response = await api.patch('/profile', values);
      setSaveSuccess(true);

      // Scroll to top to show success message
      window.scrollTo(0, 0);

      // Redirect after a short delay
      setTimeout(() => {
        navigate('/profile');
      }, 2000);
    } catch (error) {
      console.error('Error saving profile:', error);

      if (error.response && error.response.data && error.response.data.errors) {
        // Set field-specific errors
        Object.keys(error.response.data.errors).forEach(key => {
          setFieldError(key, error.response.data.errors[key][0]);
        });
      } else {
        setError('Failed to save profile. Please try again later.');
      }
    } finally {
      setSaving(false);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading profile data...</p>
      </Container>
    );
  }

  if (error && !profileData) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
        <div className="text-center mt-3">
          <Button onClick={() => window.location.reload()} variant="primary">
            Try Again
          </Button>
        </div>
      </Container>
    );
  }

  // Initial form values from profile data or defaults
  const initialValues = {
    orientation: profileData?.orientation || '',
    eyes: profileData?.eyes || '',
    body_type: profileData?.body_type || '',
    height: profileData?.height || '',
    ethnicity: profileData?.ethnicity || '',
    income: profileData?.income || '',
    net_worth: profileData?.net_worth || '',
    children: profileData?.children || '',
    relationship_status: profileData?.relationship_status || '',
    smoker: profileData?.smoker || '',
    drinker: profileData?.drinker || '',
    pot_friendly: profileData?.pot_friendly || '',
    party_drugs: profileData?.party_drugs || '',
    city: profileData?.city || '',
    region: profileData?.region || '',
    country: profileData?.country || '',
    latitude: profileData?.latitude || '0',
    longitude: profileData?.longitude || '0',
    about: profileData?.about || '',
    looking_for: profileData?.looking_for || ''
  };

  return (
    <Container className="py-4">
      <h2 className="fw-bold mb-4">Edit Profile</h2>

      {saveSuccess && (
        <Alert variant="success" className="mb-4">
          Profile saved successfully! Redirecting to your profile...
        </Alert>
      )}

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting
        }) => (
          <Form onSubmit={handleSubmit}>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-4">
                <h5 className="fw-bold mb-3">Basic Information</h5>

                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Orientation</Form.Label>
                      <Form.Select
                        name="orientation"
                        value={values.orientation}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.orientation && errors.orientation}
                      >
                        <option value="">Select orientation</option>
                        {lookups.orientation?.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.value}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.orientation}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Eye Color</Form.Label>
                      <Form.Select
                        name="eyes"
                        value={values.eyes}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.eyes && errors.eyes}
                      >
                        <option value="">Select eye color</option>
                        {lookups.eyes?.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.value}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.eyes}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Body Type</Form.Label>
                      <Form.Select
                        name="body_type"
                        value={values.body_type}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.body_type && errors.body_type}
                      >
                        <option value="">Select body type</option>
                        {lookups.body_type?.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.value}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.body_type}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Height</Form.Label>
                      <Form.Select
                        name="height"
                        value={values.height}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.height && errors.height}
                      >
                        <option value="">Select height</option>
                        {lookups.height?.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.value}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.height}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Ethnicity</Form.Label>
                      <Form.Select
                        name="ethnicity"
                        value={values.ethnicity}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.ethnicity && errors.ethnicity}
                      >
                        <option value="">Select ethnicity</option>
                        {lookups.ethnicity?.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.value}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.ethnicity}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Children</Form.Label>
                      <Form.Select
                        name="children"
                        value={values.children}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.children && errors.children}
                      >
                        <option value="">Select option</option>
                        {lookups.children?.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.value}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.children}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-4">
                <h5 className="fw-bold mb-3">Lifestyle</h5>

                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Relationship Status</Form.Label>
                      <Form.Select
                        name="relationship_status"
                        value={values.relationship_status}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.relationship_status && errors.relationship_status}
                      >
                        <option value="">Select status</option>
                        {lookups.relationship_status?.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.value}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.relationship_status}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Smoker</Form.Label>
                      <Form.Select
                        name="smoker"
                        value={values.smoker}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.smoker && errors.smoker}
                      >
                        <option value="">Select option</option>
                        {lookups.smoker?.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.value}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.smoker}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Drinker</Form.Label>
                      <Form.Select
                        name="drinker"
                        value={values.drinker}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.drinker && errors.drinker}
                      >
                        <option value="">Select option</option>
                        {lookups.drinker?.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.value}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.drinker}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Pot Friendly</Form.Label>
                      <Form.Select
                        name="pot_friendly"
                        value={values.pot_friendly}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.pot_friendly && errors.pot_friendly}
                      >
                        <option value="">Select option</option>
                        {lookups.pot_friendly?.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.value}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.pot_friendly}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-4">
                <h5 className="fw-bold mb-3">Financial</h5>

                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Income</Form.Label>
                      <Form.Select
                        name="income"
                        value={values.income}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.income && errors.income}
                      >
                        <option value="">Select income</option>
                        {lookups.income?.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.value}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.income}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Net Worth</Form.Label>
                      <Form.Select
                        name="net_worth"
                        value={values.net_worth}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.net_worth && errors.net_worth}
                      >
                        <option value="">Select net worth</option>
                        {lookups.net_worth?.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.value}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.net_worth}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-4">
                <h5 className="fw-bold mb-3">Location</h5>

                {/* Google Maps Location Selector */}
                <LocationSelector
                  initialCity={values.city}
                  initialRegion={values.region}
                  initialCountry={values.country}
                  touched={touched}
                  errors={errors}
                  onLocationSelect={(locationData) => {
                    // Only update if there's actual data to update
                    if (locationData.city || locationData.region || locationData.country) {
                      // Update only the location-related fields
                      const locationFields = ['city', 'region', 'country', 'latitude', 'longitude'];
                      const updates = {
                        city: locationData.city,
                        region: locationData.region,
                        country: locationData.country,
                        latitude: locationData.latitude.toString(),
                        longitude: locationData.longitude.toString()
                      };

                      // Set values in Formik one by one
                      locationFields.forEach(field => {
                        if (updates[field] !== values[field]) {
                          const event = {
                            target: {
                              name: field,
                              value: updates[field]
                            }
                          };
                          handleChange(event);
                        }
                      });
                    }
                  }}
                />
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-4">
                <h5 className="fw-bold mb-3">About Me</h5>

                <Form.Group className="mb-3">
                  <Form.Label>About Me</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="about"
                    value={values.about}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.about && errors.about}
                    placeholder="Tell others about yourself..."
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.about}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    {values.about ? values.about.length : 0}/1000 characters
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>What I'm Looking For</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="looking_for"
                    value={values.looking_for}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.looking_for && errors.looking_for}
                    placeholder="Describe what you're looking for in a partner or relationship..."
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.looking_for}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    {values.looking_for ? values.looking_for.length : 0}/1000 characters
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>

            <div className="d-flex justify-content-between mb-5">
              <Button
                variant="outline-secondary"
                onClick={() => navigate('/profile')}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting || saving}
              >
                {saving ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Saving...
                  </>
                ) : (
                  'Save Profile'
                )}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default ProfileEdit;
